import React from 'react';

import EntityLabel from '../generic/EntityLabel';
import ListSelect from '../generic/ListSelect';
import { FILTER_TYPES } from '../generic/Table/utils';
import { MerchantLinks } from '../merchants/Link';
import { salesChannelsEnum } from '../opportunities/constants';
import OpportunityLink from '../opportunities/OpportunityLink';
import UserLink from '../users/UserLink';
import {
  currencyRangeColumn,
  dateRangeColumn,
  filterSingleBy,
  simpleColumn,
} from '../utils/column-helpers';
import {
  ownerLink as originalOwnerLink,
  quoteLink as originalQuoteLink,
} from '../utils/columns';
import InvoiceLink from './InvoiceLink';
import SapInvoiceStatusBadge from './SapInvoiceStatusBadge';

export const amount = currencyRangeColumn({
  Header: 'Invoice Amount',
  accessor: 'amount',
  id: 'amount',
  width: 150,
});

export const idLink = {
  Header: 'Invoice',
  accessor: 'id',
  width: 80,
  Cell: row => <InvoiceLink model={row.original} />,
  filterCriteria: FILTER_TYPES.ID_VALUE,
};

export const invoiceCreationDate = dateRangeColumn(
  'Creation Date',
  'created',
  110,
);
export const lastExported = dateRangeColumn(
  'Last Exported',
  'lastExportDate',
  100,
);

export const opportunityLink = {
  Header: 'Opportunity',
  accessor: 'opportunity',
  width: 200,
  filterMethod: (filter, value) =>
    value.opportunity && filterSingleBy(filter, value.opportunity.id),
  Cell: row => <OpportunityLink model={row.value} />,
  Filter: props => <ListSelect {...props} type="opportunity" />,
  filterCriteria: FILTER_TYPES.LIST_SELECT,
  filterKey: 'opportunityId',
  sortKey: 'opportunity__name',
};

export const quoteLink = {
  ...originalQuoteLink,
  accessor: 'quote.id',
  Cell: row =>
    row.value ? <EntityLabel id={row.value} resourceType="quote" /> : null,
  sortKey: 'opportunity__primaryQuote__id',
};

export const type = simpleColumn('Type', 'type', 150);

export const requestedChanges = simpleColumn(
  'Requested Changes',
  'requestedChanges',
  150,
);

export const billingType = simpleColumn('Billing Type', 'billingType', 150);

export const ownerLink = {
  ...originalOwnerLink,
  Cell: row => <UserLink model={row.value} verbose />,
  sortKey: 'opportunity__owner__firstName',
};

export const totalBillableBudget = currencyRangeColumn({
  Header: 'Budget',
  accessor: 'totalBillableBudget',
  id: 'totalBillableBudget',
  width: 100,
  filterable: false,
  sortable: false,
});

export const totalInvoicedBudget = currencyRangeColumn({
  Header: 'Previously Invoiced',
  accessor: 'totalInvoicedBudget',
  id: 'totalInvoicedBudget',
  width: 150,
  filterable: false,
  sortable: false,
});

export const cancellationReason = {
  Header: 'Cancellation Reason',
  id: 'cancellationReason',
  accessor: 'cancellationReason',
  Cell: row => <span className="text-truncate">{row.value}</span>,
};

export const sapInvoiceNumbers = {
  Header: 'SAP Numbers',
  accessor: 'sapInvoiceNumbers',
  Cell: ({ value }) => (value || []).join(', '),
};

export const sapStatus = {
  Header: 'SAP Status',
  id: 'sapStatus',
  accessor: 'sapStatus',
  width: 100,
  Cell: row => (
    <div className="d-flex align-items-center">
      <SapInvoiceStatusBadge status={row.value} />
    </div>
  ),
};

export const sapPaidAt = dateRangeColumn('Paid On', 'sapPaidAt', 100);

export const nCode = {
  ...simpleColumn('Campaign', 'nCode', 150),
  sortable: false,
};

export const sapReversalInvoiceNumbers = {
  ...simpleColumn('SAP Revert number', 'sapReversalInvoiceNumbers', 150),
  Cell: row => row.value.join(', '),
  sortable: false,
};

export const salesChannel = simpleColumn('Sales Channel', 'salesChannel', 120);

export const poNumber = simpleColumn('PO Number', 'purchaseOrderNumber', 100);

export const merchantName = {
  Header: 'Merchants',
  id: 'merchants',
  accessor: 'merchants',
  width: 240,
  Cell: row =>
    row.original.salesChannel === salesChannelsEnum.SELF_SERVICE &&
    row.value ? (
      <MerchantLinks models={row.value} />
    ) : null,
  Filter: props => <ListSelect {...props} type="allZmsamMerchant" />,
  filterCriteria: FILTER_TYPES.LIST_SELECT,
  filterKey: 'merchantIds',
  filterable: false, // To be changed after BE adds filtering
  sortable: false,
};

export const delayReasons = {
  ...simpleColumn('Delay Reasons', 'delayReasons'),
  Cell: row => <span>{row.value?.join(', ')}</span>,
};

export const escalationStage = simpleColumn(
  'Escalation Stage',
  'escalationStage',
  150,
);

export const accountSfId = {
  ...simpleColumn('Account ID', 'accountSfId', 140),
  filterable: true,
  sortable: false,
};

export const invoiceColumns = [
  accountSfId,
  idLink,
  invoiceCreationDate,
  lastExported,
  opportunityLink,
  billingType,
  quoteLink,
  nCode,
  amount,
  ownerLink,
  type,
  totalBillableBudget,
  totalInvoicedBudget,
  sapReversalInvoiceNumbers,
  salesChannel,
  escalationStage,
  poNumber,
  merchantName,
  delayReasons,
];

export const exportedInvoiceColumns = [
  cancellationReason,
  sapInvoiceNumbers,
  sapStatus,
  sapPaidAt,
];
